import React, { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

const NAV_ITEMS = [
  { path: '/', label: 'Dashboard', icon: 'PieChart' },
  { path: '/streams', label: 'Data Streams', icon: 'Database' },
  { path: '/initiatives', label: 'Initiatives', icon: 'TrendingDown' },
  { path: '/reports', label: 'Reports & Audit', icon: 'FileText' },
  { path: '/demo', label: 'Demo', icon: 'PlayCircle' }, // Added Demo Nav
  { path: '/settings', label: 'Settings', icon: 'Settings' },
];

export default function Layout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="flex h-screen bg-[#020617] overflow-hidden selection:bg-emerald-500/30">
      {/* Sidebar */}
      <aside className={`
        fixed md:static inset-y-0 left-0 z-50 w-64 glass-panel border-l-0 border-y-0 
        transform ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} 
        md:translate-x-0 transition-transform duration-300 ease-in-out flex flex-col
      `}>
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center shadow-[0_0_15px_rgba(16,185,129,0.4)]">
              <SafeIcon name="Hexagon" className="text-slate-950 font-bold text-xl" />
            </div>
            <span className="text-xl font-bold tracking-tight text-slate-100">Carbon<span className="text-emerald-400">Frame</span></span>
          </div>
          <button className="md:hidden text-slate-400 hover:text-slate-100" onClick={() => setMobileMenuOpen(false)}>
            <SafeIcon name="X" />
          </button>
        </div>

        <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) => `
                relative flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200
                ${isActive 
                  ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 shadow-[inset_0_0_10px_rgba(16,185,129,0.1)]' 
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'}
              `}
            >
              <SafeIcon name={item.icon} className="text-lg" />
              <span className="font-medium text-sm">{item.label}</span>
              {location.pathname === item.path && (
                <motion.div layoutId="activeNav" className="absolute left-0 w-1 h-6 bg-emerald-400 rounded-r-full" />
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-800/50">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-300">
              EA
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-200 truncate">Emma Analytics</p>
              <p className="text-xs text-slate-500 truncate">Acme Corp</p>
            </div>
            <SafeIcon name="LogOut" className="text-slate-500 hover:text-slate-300 cursor-pointer w-4 h-4" />
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col h-full overflow-hidden relative">
        <header className="h-16 glass-panel border-t-0 border-x-0 flex items-center justify-between px-6 z-40">
          <div className="flex items-center gap-4">
            <button className="md:hidden text-slate-400 hover:text-slate-100" onClick={() => setMobileMenuOpen(true)}>
              <SafeIcon name="Menu" />
            </button>
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 glass-panel rounded-full border-slate-700/50 text-sm text-slate-400 focus-within:border-emerald-500/50 transition-all">
              <SafeIcon name="Search" className="w-4 h-4" />
              <input type="text" placeholder="Search workspace..." className="bg-transparent border-none outline-none text-slate-200 placeholder-slate-500 w-48" />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-2 text-sm max-w-fit px-3 py-1.5 rounded-full bg-slate-800/50 border border-slate-700 text-slate-300">
              <SafeIcon name="Calendar" className="w-4 h-4 text-emerald-400" />
              <span>FY 2024 Reporting</span>
              <SafeIcon name="ChevronDown" className="w-3 h-3 ml-1" />
            </div>
            <button className="relative p-2 text-slate-400 hover:text-slate-200 transition-colors">
              <SafeIcon name="Bell" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-auto p-4 md:p-8 relative z-0">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="h-full pb-12"
          >
            <Outlet />
          </motion.div>
        </div>

        <div className="absolute bottom-0 w-full p-3 text-center text-[10px] uppercase tracking-widest text-slate-600 glass-panel border-b-0 border-x-0 z-10 bg-slate-950/90">
          Made by LingLink AI and DigLab AI
        </div>
      </main>
      
      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-40 md:hidden" onClick={() => setMobileMenuOpen(false)} />
      )}
    </div>
  );
}